// Funnel model vs Track engagement, on identical cases: every decided
// opportunity that went through the portal. Shown inside the
// "How likelihood is calculated" dialog.
const pct = (v) => (v == null ? "–" : `${(v * 100).toFixed(0)}%`);

function RateTable({ rows, testid, predicted = false, caption }) {
  return (
    <div>
      {caption && <p className="text-[11px] font-mono uppercase tracking-wide text-stone-500 mb-1">{caption}</p>}
      <table className="w-full text-xs border border-stone-200 rounded overflow-hidden" data-testid={testid}>
      <thead>
        <tr className="bg-stone-50 text-stone-500 font-mono uppercase text-[10px]">
          <th className="px-2 py-1.5 text-left">Group</th>
          <th className="px-2 py-1.5 text-right">Quotes</th>
          {predicted && <th className="px-2 py-1.5 text-right">Predicted</th>}
          <th className="px-2 py-1.5 text-right">Actually won</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-t border-stone-100">
            <td className="px-2 py-1">{r.label}</td>
            <td className="px-2 py-1 text-right font-mono">{r.n}</td>
            {predicted && <td className="px-2 py-1 text-right font-mono">{pct(r.predicted)}</td>}
            <td className="px-2 py-1 text-right font-mono font-semibold">{pct(r.actual)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

export function EngagementBacktest({ data }) {
  if (!data) return null;
  if (!data.available) {
    return (
      <p className="text-stone-400 italic" data-testid="engagement-backtest-unavailable">
        Model vs Track: not enough decided quotes have gone through the portal yet ({data.n ?? 0} so far, 20 needed).
      </p>
    );
  }
  const a = data.auc || {};
  const chips = [
    ["Model at quote time", a.model_blind],
    ["Model with engagement", a.model_with_engagement],
    ["Model with engagement, cross-validated", a.model_with_engagement_cv],
    ["Track: sessions", a.engagement_sessions],
    ["Track: time on Costs", a.engagement_costs_dwell],
    ["Track: people viewing", a.engagement_people],
  ];
  const best = Math.max(...chips.map(([, v]) => v ?? 0));
  const weights = data.weights || [];
  return (
    <div className="space-y-3" data-testid="engagement-backtest">
      <p className="font-semibold text-slate-800">
        Model vs Track, on the same quotes: {data.n} settled opportunities went through the portal, {data.won} commissioned ({pct(data.won / data.n)}); {data.opened_n} were opened by the client.
      </p>
      <div className="flex flex-wrap gap-1.5" data-testid="engagement-backtest-auc">
        {chips.map(([label, v]) => (
          <span key={label} className={`inline-flex items-baseline gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${v != null && v === best ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-stone-200 bg-white text-stone-700"}`}>
            {label} <span className="font-mono font-semibold">{v == null ? "–" : v.toFixed(2)}</span>
          </span>
        ))}
      </div>
      <p className="text-[11px] text-stone-500">
        AUC: how often a random winner is ranked above a random loser. 0.50 is a coin toss, 1.00 is perfect. "Settled" means decided, or still open {data.maturity_days ?? 180}+ days after the quote (counted as lost). Engagement is lifetime activity on the link. The cross-validated figure scores each quote with weights learned from the others, so it is the honest one.
      </p>
      {weights.length > 0 && (
        <div>
          <p className="text-[11px] font-mono uppercase tracking-wide text-stone-500 mb-1">
            Engagement weights: {data.learned ? `learned from these ${data.n} quotes, shrunk toward the launch priors` : "launch priors (not enough settled quotes to learn yet)"}
          </p>
          <table className="w-full text-xs border border-stone-200 rounded overflow-hidden" data-testid="engagement-backtest-weights">
            <thead>
              <tr className="bg-stone-50 text-stone-500 font-mono uppercase text-[10px]">
                <th className="px-2 py-1.5 text-left">Signal</th>
                <th className="px-2 py-1.5 text-right">Quotes</th>
                <th className="px-2 py-1.5 text-right">Won</th>
                <th className="px-2 py-1.5 text-right">Prior</th>
                <th className="px-2 py-1.5 text-right">Odds now</th>
              </tr>
            </thead>
            <tbody>
              {weights.map((w) => (
                <tr key={w.key} className="border-t border-stone-100">
                  <td className="px-2 py-1">{w.label}</td>
                  <td className="px-2 py-1 text-right font-mono">{w.n}</td>
                  <td className="px-2 py-1 text-right font-mono">{w.n ? pct(w.won / w.n) : "–"}</td>
                  <td className="px-2 py-1 text-right font-mono text-stone-400">×{Number(w.prior).toFixed(2)}</td>
                  <td className={`px-2 py-1 text-right font-mono font-semibold ${w.or > 1 ? "text-emerald-700" : w.or < 1 ? "text-rose-700" : ""}`}>×{Number(w.or).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RateTable rows={data.by_track_tier || []} testid="engagement-backtest-track" caption="Track: did they open it, and did they study the costs?" />
      <RateTable rows={data.by_sessions || []} testid="engagement-backtest-sessions" caption="Track: how often did they come back?" />
      <RateTable rows={data.by_model_band || []} testid="engagement-backtest-bands" predicted caption="Funnel: model band at quote time, same quotes" />
      {data.by_model_band_with_engagement?.length > 0 && (
        <RateTable rows={data.by_model_band_with_engagement} testid="engagement-backtest-bands-eng" predicted caption="Funnel: model band with engagement, same quotes" />
      )}
    </div>
  );
}
